<template>
  <b-overlay :show="loading" opacity="0.8">
    <b-card class="rounded-lg">
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100 pb-1" style="border-bottom: 2px solid rgb(238, 238, 238);">
          <h4 class="text-dark m-0">
            Previous Medications {{ records.length > 0 ? `(${records.length})` : '' }}
          </h4>
  
          <div v-if="can('previous_medications:CanAddNew') && currentUserProfession === 'doctor'" class="d-flex align-items-center">
            <b-button size="sm" variant="link" class="p-0" @click="onAddNew">
              <feather-icon
                icon="PlusCircleIcon"
                size="25"
              />
            </b-button>
          </div>
        </div>
      </template>
      
  
      <div class="" style="height: 480px; overflow-y: scroll;">
        <b-overlay :show="loading" opacity="0.5">
          <add-or-update-patient-previous-medications
            :editing-data.sync="editingData"
            :is-add-active.sync="toggleModal"
            :patient-data="patientData"
            resource="previous_medications"
            modal-title="Previous Medications"
            @refetchData="onRefresh"
          />
  
          <div>
            <list-table
              :records="records" 
              :columns="tableColumns"
              :current-page-number="1"
              :show-import-button="false"
              :show-export-button="false"
              :show-search="false"
              :total-records="totalRecords"
            >
              <template #cell(regiment)="data">
                <p>
                  <b-badge
                    variant="info"
                    class="text-capitalize"
                  >
                    Dose
                  </b-badge>
                  <span> {{ data.item.dose }} </span> 
                </p>
                <p>
                  <b-badge
                    variant="info"
                    class="text-capitalize"
                  >
                    When
                  </b-badge>
                  <span> {{ getValueFromSource(data.item, 'drug.dose_frequency.name') }} </span> 
                </p>
                <p>
                  <b-badge
                    :variant="`info`"
                    class="text-capitalize"
                  >
                    For
                  </b-badge>
                  <span> {{ `${data.item.duration} ${startCase(data.item.duration_unit)}` }} </span> 
                </p>
                <p>
                  <b-badge
                    :variant="`info`"
                    class="text-capitalize"
                  >
                    How
                  </b-badge>
                  <span> {{ getValueFromSource(data.item, 'drug.route.name') || "-" }} </span> 
                </p>
              </template>
              <template #cell(status)="data">
                <b-badge
                  pill
                  :variant="data.item.status === 'active' ? `light-success` : 'light-danger'"
                  class="text-capitalize"
                >
                  {{ getUserStatusText(data.item.status) }}
                </b-badge>
              </template>
  
              <template #cell(duration)="data">
                <span>
                  {{ `${data.item.duration} ${startCase(data.item.duration_unit)}` }}
                </span>
              </template>
  
              <template #cell(actions)="data">
                <div v-if="currentUserProfession === 'doctor'">
                  <b-dropdown
                    variant="link"
                    no-caret
                    :right="$store.state.appConfig.isRTL"
                  >
                    <template #button-content>
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="16"
                        class="align-middle text-body"
                      />
                    </template>
    
                    <b-dropdown-item @click="onEdit(data.item)">
                      <feather-icon icon="EditIcon" />
                      <span class="align-middle ml-50">Edit</span>
                    </b-dropdown-item>
    
                    <b-dropdown-item @click="onDelete(data.item)">
                      <feather-icon icon="Trash2Icon" />
                      <span class="align-middle ml-50">Delete</span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
                <span v-else class="ml-2">-</span>
              </template>
            </list-table>
          </div>
        </b-overlay>
      </div>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BImg,
  BRow,
  BCol,
  BBadge,
  BAlert,
  BButton,
  BAvatar,
  BOverlay,
  BDropdown,
  BDropdownItem,
  BDropdownGroup
} from 'bootstrap-vue'

import { get, startCase } from 'lodash';

import Ripple from 'vue-ripple-directive'
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddOrUpdatePatientPreviousMedications from '@/@core/components/shared/admin/modals/AddOrUpdatePatientPreviousMedications.vue';

export default {
  components: {
    BImg,
    BRow,
    BCol,
    BCard,
    BBadge,
    BAlert,
    BAvatar,
    BButton,
    BOverlay,
    BDropdown,
    BDropdownItem,
    BDropdownGroup,

    ListTable,
    AddOrUpdatePatientPreviousMedications
  },
  directives: {
    Ripple,
  },
  props: {
    patientData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,

      editingData: null,
      toggleModal: false,
      
      records: [],
      tableColumns: [
        { key: 'drug.generic_name', label: 'Medication', sortable: false, class: 'name-column' },
        { key: 'regiment', label: 'Regiment', sortable: false, },
        { key: 'actions' },
      ],
    }
  },
  computed: {
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  created() {
    this.fetchAllData();
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          page: 1,
          limit: 100,
          patient_id: this.patientData._id,
        }

        const request = await this.useJwt().mainService.previousMedications.list(query);
        const { data, pagination } = request.data;
        this.records = data;
        this.totalRecords = pagination.totalRecords
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(form_data){
      try {
        this.loading = true;

        await this.useJwt().mainService.previousMedications.delete(form_data.id);
        this.$emit('refresh')
        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Record deleted successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onEdit(record){
      this.editingData = record;
      this.toggleModal = true;
    },
    onRefresh() {
      this.$emit('refresh')
      this.fetchAllData();
    },
    onAddNew(){
      this.editingData = null;
      this.toggleModal = true;
    },
    startCase
  }
}
</script>

<style scoped>
.profile-highlight {
  font-size: 18px !important;
}
.profile-highlight-title {
  font-size: 16px !important;
  color: lightgray;
  font-weight: normal;
}

.patient-name {
  font-size: 25px;
}

.name-column {
  max-width: 40px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
}
</style>

<style>
.w-10 {
  width: 13% !important;
}
</style>
